/**
 * Het raster van een maand: zes rijen van zeven dagen, maandag eerst (replica-spec §10.2).
 *
 * Waarom dit een pure functie is en geen lus in het component: de twee fouten die een maandrooster
 * maakt, zijn allebei fouten in deze berekening en geen opmaakfouten. De eerste is de week die op
 * zondag begint — `getDay()` geeft 0 voor zondag, en wie dat getal rechtstreeks als kolom gebruikt,
 * schuift elke dag van de maand één plek op. De tweede is de laatste rij die ontbreekt wanneer een
 * maand van 31 dagen op een zondag begint.
 *
 * Altijd 42 cellen, ook als de laatste rij volledig buiten de maand valt. Een rooster dat per maand
 * van hoogte verschilt, laat de kaarten eronder op en neer springen bij het bladeren — en dat is op
 * een bureauscherm met drie kaarten in een kolom meteen te zien.
 */

export interface MonthCell {
  /** De datum als "2026-09-17", zodat ze zonder tijdzonegedoe met een event te matchen is. */
  date: string;
  /** Het dagnummer, 1 tot 31. */
  day: number;
  /** Hoort deze dag bij de gevraagde maand, of komt ze van de maand ervoor of erna? */
  inMonth: boolean;
}

/** Twee cijfers, zodat "2026-9-1" nooit ontstaat. */
function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * @param month "2026-09".
 * @returns 42 cellen, de eerste altijd een maandag.
 */
export function monthGrid(month: string): MonthCell[] {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) throw new Error(`Geen maand in de vorm JJJJ-MM: ${month}`);
  const year = Number(match[1]);
  const maand = Number(match[2]);

  /*
    `Date.UTC` en niet `new Date(y, m, d)`: die tweede rekent in de lokale zone, en dan valt de
    eerste van de maand in een negatieve zone op de laatste van de vorige — met een heel rooster dat
    een dag opschuift. Hier wordt alleen gerekend met dagnummers en weekdagen, en UTC is daarvoor de
    rustige keuze.
  */
  const eersteVanDeMaand = new Date(Date.UTC(year, maand - 1, 1));

  // Maandag = 0, zondag = 6. `getUTCDay()` geeft zondag als 0; `(dag + 6) % 7` draait dat om.
  const kolomVanDeEerste = (eersteVanDeMaand.getUTCDay() + 6) % 7;

  const cellen: MonthCell[] = [];
  for (let index = 0; index < 42; index += 1) {
    const dag = new Date(Date.UTC(year, maand - 1, 1 + index - kolomVanDeEerste));
    cellen.push({
      date: `${dag.getUTCFullYear()}-${pad(dag.getUTCMonth() + 1)}-${pad(dag.getUTCDate())}`,
      day: dag.getUTCDate(),
      inMonth: dag.getUTCMonth() + 1 === maand && dag.getUTCFullYear() === year,
    });
  }
  return cellen;
}

/** De maand ervoor of erna, als "2026-09". Voor de twee pijlen boven het rooster. */
export function shiftMonth(month: string, delta: number): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) throw new Error(`Geen maand in de vorm JJJJ-MM: ${month}`);

  const totaal = Number(match[1]) * 12 + (Number(match[2]) - 1) + delta;
  return `${Math.floor(totaal / 12)}-${pad((totaal % 12) + 1)}`;
}

/** De maand waarin een dag valt, in de zone van de lezer: "2026-09". */
export function monthOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

/** De dag van een datum, in de zone van de lezer: "2026-09-17". */
export function dayOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
